/** @format */

import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME, INTRANETHOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { REST_URLS as INTRANET_URLS } from "../../../common-utilities/utils/end-points/intranet";

// Single global, admin-managed list of 2-5 users eligible to act as parallel exception
// approvers on Step 13 — shared across every CF (not per-region, not per-CF, not tied to
// the per-CF approvers already configured for steps 9/10/11).
const ExceptionApproversSetup = () => {
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${INTRANET_URLS.FILTER_USER}`,
      null,
      { appLink: "cf-approval-v2", page: 1, limit: 100 },
    ).then((res) => {
      if (Array.isArray(res?.results)) {
        setUserOptions(res.results.map((u) => ({ label: `${u.name}(${u.email})`, value: u.id })));
      }
    });

    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_EXCEPTION_APPROVERS}`)
      .then((res) => setSelectedUsers(res?.userIds || []))
      .catch(() => toast.error("Failed to load exception approvers"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = () => {
    if (selectedUsers.length < 2) {
      toast.error("Select at least 2 exception approvers");
      return;
    }
    setSaving(true);
    invokeApi(HTTP_METHODS.PUT, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_EXCEPTION_APPROVERS}`, { userIds: selectedUsers })
      .then((res) => {
        toast.success("Exception approvers saved successfully");
        setSelectedUsers(res?.userIds || selectedUsers);
      })
      .catch((err) => toast.error(err?.message || "Failed to save exception approvers"))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 600 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Exception Approvers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These 2-5 users can be asked to approve a Step 13 CF past missing documents, in parallel.
        All of them must approve for the exception to be granted.
      </Typography>
      <Autocomplete
        multiple
        options={userOptions}
        getOptionLabel={(option) => option?.label || ""}
        value={userOptions.filter((o) => selectedUsers.some((id) => +id === +o.value))}
        onChange={(e, values) => setSelectedUsers(values.map((v) => v.value))}
        size="small"
        renderInput={(params) => <TextField {...params} label="Exception Approvers" size="small" />}
      />
      <Stack direction="row" sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Stack>
    </Box>
  );
};

export default ExceptionApproversSetup;
